import React, { useCallback, useEffect, useRef, useState } from 'react';
import CommonSearch from '../common/common-search';
import CommonDataTable from '../common/common-data-table';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Checkbox, Input, InputRef, Modal, Radio, message } from 'antd';
import { api_member } from '../../api/member';
import { initMember, member } from '../../model/member';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { saleType, initSaleType } from '../../model/master';
import { sale, initSale } from '../../model/sale';
import { paymentType, initPaymentType } from '../../model/master';
import { api_product } from '../../api/product';
import { api_payment_type, api_sale, api_sale_type, api_work_shift } from '../../api/sale';
import { convertFormatDateTime, getToken, isEmpty } from '../../utils/util';
import { ReceiptItem } from '../../model/report';
import { ReceiptThermalSlip80mm } from '../report/components/thermal-slip-80mm';
import { printDocument } from '../../utils/print-utils';
import WorkShift from './work-shift';
import { useSelector } from 'react-redux';
import { userSelector } from '../../store/user/slice';
import { formatActor, UNKNOWN_ACTOR_SHORT } from '../../utils/audit-format';
import ShortCut from './shortcut';
import ScannerControl from './scanner-control';
import { ReceiverState } from '../../api/scan-pos';
import {
    REMOTE_SCAN_QUANTITY, addProductToSale, computeTotals, manualQuantity, repriceSale, resolveProductByCode,
    resolveRemoteProductByCode,
} from '../../scan-pos/cart-core';
import { LookupOutcome, MutateOutcome, RejectReason } from '../../scan-pos/receiver';
import { useScanPos } from '../../scan-pos/use-scan-pos';

const Sale: React.FC = (prop) => {

    const productCodeRef = useRef<InputRef>(null);
    const saleReceiptRef = useRef<HTMLDivElement>(null);

    // The signed-in employee, from the actor the 7C-4 auth gate filled
    // from SESSION_ME. The receipt used to name whoever `localStorage`
    // said -- see the cashier line below.
    const user = useSelector(userSelector);
    const [data, setData] = useState<sale>(initSale);
    const [isOpen, setIsOpen] = useState(false);
    const [isOpenCharge, setIsOpenCharge] = useState(false);
    const [isOpenWorkShift, setIsOpenWorkShift] = useState(false);
    const [isOpenShortcut, setIsOpenShortcut] = useState(false);
    const [lock, setLock] = useState(false);
    const [tableSelect, setTableSelect] = useState('');

    // 80mm Receipt Print Modal States
    const [isOpenReceiptModal, setIsOpenReceiptModal] = useState(false);
    const [lastReceipt, setLastReceipt] = useState<ReceiptItem | null>(() => {
        try {
            const cached = localStorage.getItem('last_completed_receipt');
            return cached ? JSON.parse(cached) : null;
        } catch {
            return null;
        }
    });
    const [autoPrintReceipt, setAutoPrintReceipt] = useState<boolean>(() => {
        return localStorage.getItem('auto_print_receipt') === 'true';
    });

    const [pauseData, setPauseData] = useState<any[]>([]);

    const [vat, setVat] = useState(-1);
    const [paymentType, setPaymentType] = useState(1);
    const [paymentTypeList, setPaymentTypeList] = useState<paymentType[]>([]);

    const [saleType, setSaleType] = useState(1);
    const [saleTypeList, setSaleTypeList] = useState<saleType[]>([]);

    const [memberCode, setMemberCode] = useState('');
    const [member, setMember] = useState<member>(initMember);
    const [memberList, setMemberList] = useState<member[]>([]);

    const [productCode, setProductCode] = useState('');
    const [productQty, setProductQty] = useState(1);
    const [productData, setProductData] = useState<any[]>([]);
    const [productList, setProductList] = useState<any[]>([]);

    const [price, setPrice] = useState(0);
    const [vatAmount, setVatAmount] = useState(0);
    const [discount, setDiscount] = useState(0);
    const [discountBill, setDiscountBill] = useState(0);
    const [totalPrice, setTotalPrice] = useState(0);

    const [receive, setReceive] = useState(0);
    const [change, setChange] = useState(0);
    const [remark, setRemark] = useState('');

    // Scan POS P6 -- the Cart as of the LAST commit, readable synchronously.
    // Every Cart write goes through setSale/commitSale, which update this
    // ref at once, so a Remote scan arriving between renders builds on the
    // latest Cart (and the second of two scans on the first one's result).
    const dataRef = useRef<sale>(initSale);
    const saleTypeRef = useRef(saleType);
    saleTypeRef.current = saleType;
    const productListRef = useRef<any[]>(productList);
    productListRef.current = productList;
    const [initialized, setInitialized] = useState(false);
    const [scannerOverlay, setScannerOverlay] = useState(false);
    const [resumeTick, setResumeTick] = useState(0);

    const setSale = (next: any) => {
        dataRef.current = next;
        setData(next);
    };

    const productColumn: any = [
        { title: 'รหัสสินค้า', dataIndex: 'product_code', key: 'product_code', width: '160px', edit: false },
        { title: 'ชื่อสินค้า', dataIndex: 'product_name', key: 'product_name', width: '230px', edit: false },
        { title: 'จำนวน', dataIndex: 'quantity', key: 'quantity', type: 'INPUT', width: '100px', edit: true },
        { title: 'น้ำหนัก', dataIndex: 'weight', key: 'weight', type: 'INPUT', width: '100px', edit: true },
        { title: 'ราคา', dataIndex: 'price', key: 'price', width: '100px', edit: false },
        { title: 'ส่วนลด', dataIndex: 'discount', key: 'discount', type: 'INPUT', width: '100px', edit: true },
        { title: 'รวม', dataIndex: 'total_price', key: 'price', width: '100px', edit: false },
    ]

    const setDefaultValue = () => {
        setVat(-1);
        setPaymentType(1);
        setSaleType(1);
        setMemberCode('');
        setMember(initMember);

        setProductCode('');
        setProductData([]);

        setPrice(0);
        setVatAmount(0);
        setDiscount(0);
        setDiscountBill(0);
        setTotalPrice(0);

        setReceive(0);
        setChange(0);
        setRemark('');
        setSale(initSale);
    }

    const checkWorkShift = async () => {
        let token = '';
        await getToken().then((item: string) => {
            token = item;
        });
        // let getWorkShift: any = await api_work_shift('GET_BY_CONDITION', {"device": token, "create_date": 1});
        // if (getWorkShift == null || getWorkShift == undefined || !('id' in getWorkShift)) {
        //     setLock(true);
        //     setIsOpenWorkShift(true);
        // } else {
        //     if (getWorkShift['status'] == 0) {
        //         Modal.warning({
        //             ...getConfigModal('แจ้งเตือน', 'กรุณาเปิดกะก่อนทำรายการ')
        //         });
        //     }
        // }
    }

    const init = async () => {
        let pause_data = isEmpty(localStorage.getItem('pause')) ? [] : JSON.parse(localStorage.getItem('pause') || "[]");
        setPauseData(pause_data);
        setDefaultValue();

        // Four independent sources. A till that cannot reach the member
        // list must still be able to sell, so each is fetched on its own
        // and a failure is reported rather than allowed to abort the rest
        // -- which is what the old sequential chain did the moment these
        // APIs stopped swallowing their errors.
        const load = async (what: string, fetch: () => Promise<any>, set: (v: any[]) => void) => {
            try {
                const res = await fetch();
                const rows = Array.isArray(res) ? res : (res?.items || []);
                set(Array.isArray(rows) ? rows : []);
            } catch (e: any) {
                set([]);
                message.error(e?.response?.data?.message || e?.message || `โหลด${what}ไม่สำเร็จ`);
            }
        };
        await load('ประเภทการขาย', () => api_sale_type('LIST', {}), setSaleTypeList);
        await load('ช่องทางชำระเงิน', () => api_payment_type('LIST', {}), setPaymentTypeList);
        await load('รายชื่อสมาชิก', () => api_member('LIST', {}), setMemberList);
        await load('รายการสินค้า', () => api_product('LIST', {}), setProductList);
        setInitialized(true);
    }

    const getConfigModal = (titile: string, content: string) => {
        return {
            title: titile,
            icon: <ExclamationCircleFilled rev={undefined} />,
            content: content,
            okText: 'ตกลง',
            cancelText: 'ยกเลิก',
            centered: true
        }
    }

    const setInputData = (object: any) => {
        setProductData(object.detail != null ? object.detail : []);
        setDiscountBill(object.discount_bill !== undefined ? object.discount_bill : 0);
        setVat(object.vat !== undefined ? object.vat : -1);
        calculate(object);
    }

    const onDataChange = (e: any) => {
        let key: string = String(e.target.name);
        let value: any = (
            e.target.type === 'checkbox'
                ? (e.target.checked ? 1 : 0)
                : e.target.type === 'radio' ? Number(e.target.value)
                : typeof(e.target.value) === 'number' ? Number(e.target.value)
                : String(e.target.value)
        );
        let object: any = { ...dataRef.current, [key]: value };

        if (key === 'vat') {
            setVat(Number(value));
            object['vat'] = Number(value);
        } else if (key === 'discount_bill') {
            let numVal = parseFloat(value) || 0;
            setDiscountBill(numVal);
            object['discount_bill'] = numVal;
        }

        setSale(object);
        setInputData(object);
    }

    const onChargeChange = (e: any) => {
        let key: string = String(e.target.name);
        let value: any = (
            e.target.type === 'checkbox'
                ? (e.target.checked ? 1 : 0)
                : e.target.type === 'radio' ? Number(e.target.value)
                : typeof(e.target.value) === 'number' ? Number(e.target.value)
                : String(e.target.value)
        );
        let object: any = { ...dataRef.current, [key]: value };
        
        let pType = object.payment_type !== undefined ? object.payment_type : paymentType;
        let rec = pType === 2 ? totalPrice : (parseFloat(object.receive) || 0);
        let cal = parseFloat((rec - totalPrice).toFixed(2));
        let chg = cal < 0 ? 0 : cal;
        object['change'] = chg;
        object['receive'] = rec;

        setSale(object);
        setPaymentType(pType);
        setReceive(rec);
        setChange(chg);
        setRemark(object.remark || '');
    }

    const onOpenSelect = (table: string) => {
        enterReceiverState('PAUSED');
        setTableSelect(table);
        setIsOpen(true);
    }

    const onMemberChange = (e: any) => {
        setMemberCode(e.target.value);
    }

    const onSearchInputChange = async (e: any, flag: string) => {
        if (e.keyCode == 13) {
            if (flag == 'M') {
                let getMember = memberList.filter(x => { return x['code'] == e.target.value });
                if (getMember.length > 0) {
                    onManageMember(getMember[0]);
                } else {
                    Modal.warning({
                        ...getConfigModal('แจ้งเตือน', 'ไม่พบสมาชิกที่ค้นหา')
                    });
                }
            } else if (flag == 'P') {
                let code = (e.target.name == 'product_qty' ? productCode : e.target.value).trim();
                if (code) {
                    // The shared lookup (cart-core) -- the same one a Remote scan uses.
                    const found = await resolveProductByCode(code, productListRef.current);
                    if (found.status === 'FOUND') {
                        onManageProduct('A', found.product);
                    } else if (found.status === 'NOT_FOUND') {
                        Modal.warning({
                            ...getConfigModal('แจ้งเตือน', 'ไม่พบสินค้าที่ค้นหา')
                        });
                    } else {
                        Modal.error({
                            ...getConfigModal('ข้อผิดพลาด', 'ค้นหาสินค้าไม่สำเร็จ กรุณาตรวจสอบการเชื่อมต่อ')
                        });
                    }
                }
            }

            setMemberCode('');
            setProductCode('');
            setProductQty(1);
            productCodeRef.current?.focus();
        }
    }

    const onManageMember = (e: any) => {
        setMember({
            key: 1,
            id: e['id'],
            code: e['code'],
            firstname: e['firstname'],
            lastname: e['lastname'],
            tel: e['tel'],
            email: e['email'],
            tax_id: e['tax_id'],
            address: e['address'],
            remark: e['remark'],
            member_group: e['member_group'],
            member_group_name: e['member_group_name'],
            status: e['status'],
            create_date: e['create_date']
        });
    }

    const onProductChange = (e: any) => {
        setProductCode(e.target.value);
    }

    const onProductQtyChange = (e: any) => {
        setProductQty(isEmpty(e.target.value) ? 0 : parseInt(e.target.value));
    }

    /** Installs [next] as the current Cart (ref first, then the screen). */
    const commitSale = (next: any) => {
        setSale(next);
        setInputData(next);
    };

    const onManageProduct = (key: string, e: any) => {
        if (key == 'A') {
            // The shared Cart core (cart-core): the same add a Remote scan makes,
            // with the manual quantity box instead of the Remote +1.
            commitSale(addProductToSale(dataRef.current, e, manualQuantity(productQty), saleType));
        } else if (key == 'E') {
            let object: any = JSON.parse(JSON.stringify(dataRef.current));
            var index = object.detail.findIndex((x: any) => {
                return x['key'] == e['key']
            });
            object.detail[index] = e;
            commitSale(object);
        }
        setProductQty(1);
    }

    const onActionProduct = (e: any) => {
        let object: any = dataRef.current;
        let count = 0;

        if ('mode' in e[0]) {
            let mode = e[0].mode;
            let key = e[0].key;

            if (mode == 'remove') {
                let detail: any = [];

                object.detail.forEach((x: any) => {
                    if (x.key != key) {
                        count++;
                        x['key'] = count;
                        detail.push(x);
                    }
                });
                object.detail = detail;

                setSale(object);
                setInputData(object);
            }
        } else {
            object.detail = e;
            updateProduct(object);
        }
    }

    const updateProduct = (object: any) => {
        const next = repriceSale(object);
        setSale(next);
        setInputData(next);
    }

    const setFooter = () => {
        return <>
            <br />
            <Button className='btn-cancel' onClick={() => { setIsOpen(false); setTableSelect(''); setIsOpenWorkShift(false); setIsOpenShortcut(false);}}>ยกเลิก</Button>
        </>
    }

    const setFooterCharge = () => {
        return <>
            <br />
            <Button className='btn-common' onClick={() => { onValidateCharge() }}>คิดเงิน</Button>
            <Button className='btn-cancel' onClick={() => { setIsOpenCharge(false); }}>ยกเลิก</Button>
        </>
    }

    const handleSelected = (e: any) => { 
        if (e.table == 'member') {
            onManageMember(e);
        } else if (e.table == 'product') {
            onManageProduct('A', e);
        } else if (e.table == 'pause') {
            let pause_data: any = JSON.parse(localStorage.getItem('pause') || "[]");
            pause_data = pause_data.filter((x: any) => { return Object.keys(x) != e['date'][0] });
            localStorage.setItem('pause', JSON.stringify(pause_data));

            let object = pauseData.filter((x: any) => { return Object.keys(x) == e['date'][0]})[0][e['date'][0]];
            setSale(object);
            setInputData(object);
            setPauseData(pause_data);

            let member = isEmpty(object['member']) ? 0 : object['member'];
            let getMember = memberList.filter(x => { return x['id'] == member });
            if (getMember.length > 0) {
                onManageMember(getMember[0]);
            }

            setSaleType(object['sale_type']);
        }
        setIsOpen(false);
    }

    const onSaleTypeChange = (e: any) => {
        let object: any = dataRef.current;
        setSaleType(e.target.value);

        object['sale_type'] = e.target.value;
        updateProduct(object);
    }

    const calculate = (object: any) => {
        const totals = computeTotals(object, vat);
        setPrice(totals.price);
        setDiscount(totals.discount);
        setDiscountBill(totals.discountBill);
        setVatAmount(totals.vatAmount);
        setTotalPrice(totals.totalPrice);
    }

    const onChargeMoney = () => {
        let object = JSON.parse(JSON.stringify(data));
        let product_price_status = true;
        if (!productData || productData.length === 0) {
            Modal.warning({
                ...getConfigModal('แจ้งเตือน', 'กรุณาเพิ่มรายการสินค้าก่อนคิดเงิน')
            });
            return;
        }

        object.detail.forEach((x: any) => {
            if (x['total_price'] < 0) {
                product_price_status = false;
                return false;
            }
        });

        if (totalPrice > 0 && product_price_status) {
            let initialReceive = paymentType === 2 ? totalPrice : (receive > 0 ? receive : totalPrice);
            let initialChange = paymentType === 2 ? 0 : (initialReceive >= totalPrice ? parseFloat((initialReceive - totalPrice).toFixed(2)) : 0);
            setReceive(initialReceive);
            setChange(initialChange);
            enterReceiverState('LOCKED');          // local LOCKED + Backend, BEFORE the payment UI
            setIsOpenCharge(true);
        } else {
            Modal.warning({
                ...getConfigModal('แจ้งเตือน', 'ราคารวมไม่ถูกต้อง')
            });
        }
    }

    const onValidateCharge= () => {
        if (receive < totalPrice) {
            Modal.warning({
                ...getConfigModal('แจ้งเตือน', 'จำนวนเงินไม่ถูกต้อง')
            });
        } else {
            onSave();
        }
    }

    const onSave = async () => {
        let operation = 'INSERT';
        let key_id = undefined;
        let body = JSON.parse(JSON.stringify(data));
        if (data.id > 0) {
            operation = 'UPDATE';
            key_id = data.id;
        }

        body['detail'] = productData;
        body['receive'] = paymentType == 2 ? totalPrice : receive;
        body['change'] = paymentType == 2 ? 0 : change;
        body['price'] = price;
        body['vat_amount'] = vatAmount;
        body['discount'] = discount;
        body['discount_bill'] = discountBill;
        if (body['detail'].length > 0 &&
            !isEmpty(body['receive']) && body['receive'] > 0 && !isEmpty(body['change']) && body['change'] >= 0 && !isEmpty(body['price']) && body['price'] > 0
            && !isEmpty(body['vat_amount']) && body['vat_amount'] >= 0 && !isEmpty(body['discount']) && body['discount'] >= 0 && !isEmpty(body['discount_bill']) && body['discount_bill'] >= 0) {
            body['vat'] = body['vat'] < 0 ? null : body['vat'];
            body['payment_type'] = paymentType;
            body['sale_type'] = saleType;
            body['point'] = isEmpty(body['point']) ? 0 : body['point'];
            body['remark'] = isEmpty(body['remark']) ? null : body['remark'];
            body['member'] = isEmpty(member.id) || member.id < 1 ? null : member.id;

            let res = await api_sale(operation, body, key_id);
            // `res` is `{status, document_no, id}` from the current Backend
            // and a bare `1` from one that predates it. Anything else is not
            // a success: the old check accepted EVERY object, including the
            // empty array this client returns when nothing came back.
            const saved = res === 1 || res === '1' || Number(res?.status) === 1 || res?.status === 200;
            if (saved) {
                const paymentTypeName = paymentTypeList.find(p => p.id === paymentType)?.name || (paymentType === 1 ? 'เงินสด' : paymentType === 2 ? 'โอนเงิน' : 'อื่นๆ');
                // The number the database gave this sale. It used to fall
                // through to `INV-<today>-<random>` -- and always did, because
                // Backend returned only a status code, so every receipt a
                // customer took home carried a number matching no row in
                // `invoice`. Blank now if Backend did not say, and the slip
                // prints "ไม่ทราบ" rather than a number nobody can look up.
                const billNo = (typeof res === 'object' && res?.document_no)
                    ? String(res.document_no)
                    : (data.document_no || '');
                // Was `localStorage 'fullname' || 'username' || 'แคชเชียร์ 01'`.
                // Neither key is written anywhere in this codebase, so every
                // receipt named "แคชเชียร์ 01". The cashier is whoever is
                // signed in.
                const cashierName = formatActor(user?.name, UNKNOWN_ACTOR_SHORT);
                
                const completedReceipt: ReceiptItem = {
                    // `Date.now()` used to stand in for a missing id, giving
                    // the cached receipt an identifier no invoice has.
                    id: (typeof res === 'object' && res?.id) ? res.id : 0,
                    bill_no: billNo,
                    create_date: convertFormatDateTime(new Date(), false),
                    cashier_name: cashierName,
                    member_name: member.id > 0 ? `${member.firstname} ${member.lastname}` : undefined,
                    member_code: member.id > 0 ? member.code : undefined,
                    sale_type_name: saleTypeList.find(s => s.id === saleType)?.name || 'ขายหน้าร้าน',
                    payment_type_name: paymentTypeName,
                    payment_type_id: paymentType,
                    total_price: price,
                    discount: discount,
                    discount_bill: discountBill,
                    vat_amount: vatAmount,
                    net_price: totalPrice,
                    receive: paymentType === 2 ? totalPrice : receive,
                    change: paymentType === 2 ? 0 : change,
                    point: data.point || 0,
                    remark: remark,
                    status: 1,
                    item_count: productData.reduce((acc: number, item: any) => acc + (Number(item.quantity) || 1), 0),
                    items: productData.map((item: any) => ({
                        product_code: item.product_code || '',
                        product_name: item.product_name || '',
                        quantity: Number(item.quantity) || 1,
                        price: Number(item.price) || 0,
                        discount: Number(item.discount) || 0,
                        total_price: Number(item.total_price) || (Number(item.quantity || 1) * Number(item.price || 0))
                    }))
                };

                setLastReceipt(completedReceipt);
                try {
                    localStorage.setItem('last_completed_receipt', JSON.stringify(completedReceipt));
                } catch (e) {
                    console.error(e);
                }

                enterReceiverState('PAUSED');      // receipt / next-sale transition
                setIsOpenCharge(false);
                setIsOpenReceiptModal(true);

                if (autoPrintReceipt) {
                    setTimeout(() => {
                        if (saleReceiptRef.current) {
                            printDocument(saleReceiptRef.current, {
                                documentType: 'thermal-80mm',
                                title: `ใบเสร็จรับเงิน ${completedReceipt.bill_no || ''}`
                            });
                        }
                    }, 400);
                }
            } else {
                Modal.error({
                    ...getConfigModal('ผิดพลาด', operation === 'UPDATE' ? 'แก้ไขข้อมูลไม่สำเร็จ' : 'เพิ่มข้อมูลไม่สำเร็จ'),
                });
            }
        } else {
            Modal.warning({
                ...getConfigModal('แจ้งเตือน', 'กรุณากรอกข้อมูลให้ครบถ้วน')
            });
        } 
    }

    const handleCloseReceiptModal = () => {
        enterReceiverState('PAUSED');
        setIsOpenReceiptModal(false);
        setDefaultValue();
        setResumeTick((t) => t + 1);
    };

    const onCancelSale = () => {
        enterReceiverState('PAUSED');
        setDefaultValue();
        setResumeTick((t) => t + 1);
    };

    const handleReprintLastBill = () => {
        if (!lastReceipt) {
            Modal.info({
                ...getConfigModal('แจ้งเตือน', 'ยังไม่มีประวัติบิลที่ชำระเงินสำเร็จในรอบนี้'),
            });
            return;
        }
        setIsOpenReceiptModal(true);
    };

    const handleToggleAutoPrint = (checked: boolean) => {
        setAutoPrintReceipt(checked);
        try {
            localStorage.setItem('auto_print_receipt', String(checked));
        } catch (e) {
            console.error(e);
        }
    };

    const onPause = async () => {
        let body = JSON.parse(JSON.stringify(data));
        let pause_data = isEmpty(localStorage.getItem('pause')) ? [] : JSON.parse(localStorage.getItem('pause') || "[]");

        if (pause_data.length < 5) {
            if (body['detail'].length > 0) {
                let pause_data_obj: any = {}
                let date = convertFormatDateTime(new Date(), false)

                body['detail'] = productData;
                body['receive'] = paymentType == 2 ? totalPrice : receive;
                body['change'] = paymentType == 2 ? 0 : change;
                body['price'] = price;
                body['vat_amount'] = vatAmount;
                body['discount'] = discount;
                body['discount_bill'] = discountBill;
                body['vat'] = body['vat'] < 0 ? null : body['vat'];
                body['payment_type'] = paymentType;
                body['sale_type'] = saleType;
                body['point'] = isEmpty(body['point']) ? 0 : body['point'];
                body['remark'] = isEmpty(body['remark']) ? null : body['remark'];
                body['member'] = isEmpty(member.id) || member.id < 1 ? null : member.id;
                pause_data_obj[date] = body;
                pause_data.push(pause_data_obj);
                localStorage.setItem('pause', JSON.stringify(pause_data))

                enterReceiverState('PAUSED');
                setPauseData(pause_data);
                setDefaultValue();
                setResumeTick((t) => t + 1);
            }
        } else {
            Modal.warning({
                ...getConfigModal('แจ้งเตือน', 'รายการพักบิลถึงที่กำหนดแล้ว')
            });
        }
    }

    useEffect(() => {
        checkWorkShift();
        init();
    }, []);

    // ------------------------------------------------------------ Scan POS P6
    // What the Remote Scanner receiver may do right now, from what is on screen.
    const desiredReceiverState: ReceiverState = !initialized ? 'WAITING'
        : isOpenCharge ? 'LOCKED'
        : (isOpenReceiptModal || isOpen || isOpenWorkShift || isOpenShortcut || scannerOverlay) ? 'PAUSED'
        : 'READY';
    const desiredRef = useRef<ReceiverState>(desiredReceiverState);
    desiredRef.current = desiredReceiverState;

    const scan = useScanPos({
        lookup: async (barcode: string): Promise<LookupOutcome> => {
            // Latency R1: a Remote scan tries the already-loaded list first;
            // typed / physical-scanner codes keep resolveProductByCode.
            const found = await resolveRemoteProductByCode(barcode, productListRef.current);
            if (found.status === 'FOUND') return { found: true, product: found.product, source: found.source };
            return { found: false, reason: found.status === 'NOT_FOUND' ? 'PRODUCT_NOT_FOUND' : 'PRODUCT_LOOKUP_UNAVAILABLE' };
        },
        mutate: (product: any): MutateOutcome => {
            let next: any;
            try {
                next = addProductToSale(dataRef.current, product, REMOTE_SCAN_QUANTITY, saleTypeRef.current);
            } catch {
                return { status: 'NOT_APPLIED' };              // computed nothing: the Cart is untouched
            }
            try {
                commitSale(next);
            } catch {
                return { status: 'AMBIGUOUS' };                // may be half-installed: never claim either way
            }
            return { status: 'APPLIED', product };
        },
        onApplied: (product: any) => {
            message.success(`📱 ${product?.product_name || 'สินค้า'} +1`);
        },
        onRejected: (reason: RejectReason) => {
            if (reason === 'PRODUCT_NOT_FOUND') message.warning('📱 ไม่พบสินค้าที่สแกนจากมือถือ');
            else if (reason === 'PRODUCT_LOOKUP_UNAVAILABLE') message.warning('📱 ค้นหาสินค้าที่สแกนไม่สำเร็จ');
            else if (reason === 'CART_MUTATION_FAILED') message.warning('📱 เพิ่มสินค้าที่สแกนไม่สำเร็จ');
            else message.info('📱 หน้าขายยังไม่พร้อมรับการสแกน');
        },
        onAmbiguous: () => {
            message.error('📱 ไม่แน่ใจว่าสินค้าที่สแกนถูกเพิ่มแล้วหรือไม่ กรุณาตรวจสอบรายการ');
        },
        isPageIdle: () => desiredRef.current === 'READY',
    });

    /** Synchronous: the local receiver gate changes BEFORE the next UI opens. */
    const enterReceiverState = (state: ReceiverState) => {
        if (scan.enabled) scan.setReceiverState(state);
    };

    const onScannerOverlay = useCallback((open: boolean) => {
        if (open && scan.enabled) scan.setReceiverState('PAUSED');
        setScannerOverlay(open);
    }, [scan.enabled]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (scan.enabled) scan.setReceiverState(desiredReceiverState);
    }, [scan.enabled, scan.snapshot?.role, scan.snapshot?.generation, desiredReceiverState, resumeTick]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <>
            <div className='saleBox'>
                <div className='leftBox'>
                    <div className='memberBox'>
                        <div className='memberSearchSection'>
                            <div className='memberInputGroup'>
                                <label className='searchGroupLabel'>👤 สมาชิก</label>
                                <div className='inputWithAction'>
                                    <Input 
                                        name="member_code" 
                                        value={memberCode} 
                                        placeholder="ป้อนรหัสสมาชิก หรือ เบอร์โทร" 
                                        onChange={onMemberChange} 
                                        onKeyUp={(e: any) => {onSearchInputChange(e, 'M')}} 
                                    />
                                    <Button className='btn-search-icon' onClick={() => {onOpenSelect('member')}}>
                                        <FontAwesomeIcon icon="magnifying-glass" />
                                    </Button>
                                </div>
                            </div>
                            { member.id > 0 && (
                                <div className='selectedMemberBadge'>
                                    <span className='memberName'>👤 {member.firstname} {member.lastname}</span>
                                    {member.member_group_name && <span className='memberGroupTag'>({member.member_group_name})</span>}
                                    <Button className='btn-remove-member' onClick={() => {setMember(initMember)}} title="ยกเลิกสมาชิก">
                                        <FontAwesomeIcon icon="times" />
                                    </Button>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className='productBox'>
                        <div className='conditionProductBox'>
                            <div className='saleTypeRadioRow'>
                                <Radio.Group name="sale_type" value={saleType} onChange={onSaleTypeChange}>
                                    {
                                        (saleTypeList || []).map((obj: any) => {
                                            return <Radio.Button key={obj.key} value={obj.id}>{obj.name}</Radio.Button>
                                        })
                                    }
                                </Radio.Group>
                            </div>
                            <div className='productSearchSection'>
                                <div className='productInputGroup'>
                                    <label className='searchGroupLabel'>📦 ค้นหาสินค้า</label>
                                    <div className='inputWithAction'>
                                        <Input 
                                            ref={productCodeRef} 
                                            name="product_code" 
                                            value={productCode} 
                                            placeholder="ยิงบาร์โค้ด หรือ ป้อนรหัสสินค้า" 
                                            onChange={onProductChange} 
                                            onKeyUp={(e: any) => {onSearchInputChange(e, 'P')}} 
                                        />
                                        <Button className='btn-search-icon' onClick={() => {onOpenSelect('product')}}>
                                            <FontAwesomeIcon icon="magnifying-glass" />
                                        </Button>
                                    </div>
                                </div>
                                <div className='qtyInputGroup'>
                                    <label className='searchGroupLabel'>จำนวน</label>
                                    <Input 
                                        name="product_qty" 
                                        type="number"
                                        min={1}
                                        value={productQty} 
                                        onChange={onProductQtyChange} 
                                        onKeyUp={(e: any) => {onSearchInputChange(e, 'P')}} 
                                    />
                                </div>
                            </div>
                        </div>
                        {scan.enabled && scan.snapshot && (
                            <div className='scannerRow'>
                                <ScannerControl
                                    snapshot={scan.snapshot}
                                    onTransfer={scan.transfer}
                                    onRetry={scan.retryChannel}
                                    onOverlay={onScannerOverlay}
                                />
                            </div>
                        )}
                        <CommonDataTable columns={productColumn} data={[...productData]} operation='OUT_TABLE' scollX={768} onChange={onActionProduct} />
                    </div>
                </div>
                <div className='rightBox'>
                    <div className='rightHeaderBox'>
                        <div className='vatRadioContainer'>
                            <Radio.Group 
                                buttonStyle="solid" 
                                style={{display: 'flex', width: '100%', marginBottom: '8px'}} 
                                name="vat" 
                                value={vat} 
                                onChange={onDataChange}
                            >
                                <Radio.Button value={-1} style={{flex: 1, textAlign: 'center'}}>ไม่คิด</Radio.Button>
                                <Radio.Button value={0} style={{flex: 1, textAlign: 'center'}}>Vat Out</Radio.Button>
                                <Radio.Button value={1} style={{flex: 1, textAlign: 'center'}}>Vat In</Radio.Button>
                            </Radio.Group>
                        </div>
                        <Button className='btn-common' style={{width: '100%', height: '36px', marginBottom: '10px'}} onClick={() => {onOpenSelect('pause')}}>รายการพักบิล ({pauseData.length})</Button>
                        
                        <div className='chargeMoneyBox'>
                            <div className='formInput'>
                                <label>ราคา</label>
                                <Input name="price" value={price} placeholder={'กรุณาป้อนราคา'} disabled />
                            </div>
                            <div className='formInput'>
                                <label>ภาษีมูลค่าเพิ่ม</label>
                                <Input name="vat_amount" value={vatAmount} placeholder={'กรุณาป้อนภาษีมูลค่าเพิ่ม'} disabled />
                            </div>
                            <div className='formInput'>
                                <label>ส่วนลด</label>
                                <Input name="discount" value={discount} placeholder={'กรุณาป้อนส่วนลด'} disabled />
                            </div>
                            <div className='formInput'>
                                <label>ส่วนลดท้ายบิล</label>
                                <Input name="discount_bill" value={discountBill} placeholder={'กรุณาป้อนส่วนลดท้ายบิล'} onChange={onDataChange} />
                            </div>
                            <div className='formInputTotal'>
                                <label>รวม</label>
                                <Input name="total_price" value={totalPrice} placeholder={'กรุณาป้อนราคารวม'} disabled />
                            </div>
                            <Button className='btn-common' style={{width: '100%', height: '44px', fontSize: '1.15em', marginBottom: '8px'}} onClick={onChargeMoney}>คิดเงิน</Button>
                        </div>
                    </div>
                    
                    <div className='rightFooterBox'>
                        <div className='btn-sale-menu-box'>
                            <Button className='btn-back' onClick={onPause}>พักบิล</Button>
                            <Button className='btn-cancel-sale' onClick={onCancelSale}>ยกเลิก</Button>
                            <Button className='btn-common' onClick={()=>{ enterReceiverState('PAUSED'); setIsOpenShortcut(true); }}>เมนูลัด</Button>
                            <Button className='btn-common' onClick={()=>{ enterReceiverState('PAUSED'); setLock(false); setIsOpenWorkShift(true);}}>เปิด - ปิดกะ</Button>
                            <Button 
                                className='btn-common' 
                                style={{ width: '100%', flex: '1 1 100%', background: '#FFF3EE', borderColor: '#FF7A59', color: '#FF7A59', fontWeight: 600, height: '36px', borderRadius: '10px' }}
                                onClick={handleReprintLastBill}
                                data-testid="btn-reprint-last-receipt"
                            >
                                <FontAwesomeIcon icon="print" style={{ marginRight: '6px' }} />
                                พิมพ์บิลล่าสุด (80mm)
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            <Modal
                closeIcon={false}
                centered
                open={isOpen}
                footer={setFooter()}
                width={780}
                destroyOnClose={true}
            >
                <CommonSearch table={tableSelect} onSelect={handleSelected} />
            </Modal>

            <Modal
                closeIcon={false}
                centered
                open={isOpenCharge}
                footer={setFooterCharge()}
                width={'30%'}
                destroyOnClose={true}
            >
                <Radio.Group style={{display: 'flex', width: '100%', marginBottom: '20px'}} name="payment_type" value={paymentType} onChange={onChargeChange}>
                    {
                        (paymentTypeList || []).map((obj: any) => {
                            return <Radio key={obj.key} value={obj.id}>{obj.name}</Radio>
                        })
                    }
                </Radio.Group>
                <div className='formInput'>
                    <label style={{fontSize: '20px'}}>ราคา</label>
                    <Input style={{fontSize: '20px'}} name="total_price" value={totalPrice} disabled />
                </div>
                <div className='formInput'>
                    <label style={{fontSize: '20px'}}>รับเงิน</label>
                    <Input style={{fontSize: '20px'}} name="receive" value={receive} onChange={onChargeChange} disabled={paymentType == 2} />
                </div>
                {paymentType == 1 ? <div className='formInput'>
                    <label style={{fontSize: '20px'}}>เงินทอน</label>
                    <Input style={{fontSize: '20px'}} name="change" value={change} onChange={onChargeChange} disabled />
                </div> : null}
                <div className='formInput'>
                    <label style={{fontSize: '20px'}}>หมายเหตุ</label>
                    <Input style={{fontSize: '20px'}} name="remark" value={remark} onChange={onChargeChange} />
                </div>
            </Modal>

            {/* Modal: 80mm Receipt Slip & Checkout Completion */}
            <Modal
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '18px', fontWeight: 700 }}>
                        <span>🧾</span>
                        <span>สลิปใบเสร็จรับเงิน (80mm Thermal Slip)</span>
                    </div>
                }
                centered
                open={isOpenReceiptModal}
                onCancel={handleCloseReceiptModal}
                footer={null}
                width={460}
                style={{ maxWidth: '95vw' }}
                destroyOnClose={true}
                data-testid="modal-receipt-80mm"
            >
                {lastReceipt && (
                    <>
                        <div className="sale-receipt-success-banner">
                            <div className="sale-receipt-success-icon">✅</div>
                            <div className="sale-receipt-success-title">ชำระเงินเรียบร้อยแล้ว</div>
                            {lastReceipt.change > 0 ? (
                                <div className="sale-receipt-change-box">
                                    <span className="sale-receipt-change-label">💵 เงินทอน:</span>
                                    <span className="sale-receipt-change-amount">฿{lastReceipt.change.toLocaleString()}</span>
                                </div>
                            ) : (
                                <div style={{ fontSize: '13px', color: '#047857', marginTop: '4px', fontWeight: 600 }}>
                                    ชำระครบถ้วนพอดี (ไม่ต้องทอนเงิน)
                                </div>
                            )}
                        </div>

                        <div className="sale-receipt-preview-container">
                            <ReceiptThermalSlip80mm ref={saleReceiptRef} receipt={lastReceipt} />
                        </div>

                        <div className="sale-receipt-footer-actions">
                            <Checkbox 
                                checked={autoPrintReceipt} 
                                onChange={(e) => handleToggleAutoPrint(e.target.checked)}
                                style={{ fontWeight: 500 }}
                            >
                                ⚡ พิมพ์สลิปอัตโนมัติ
                            </Checkbox>

                            <div style={{ display: 'flex', gap: '8px' }}>
                                <Button 
                                    type="primary"
                                    icon={<FontAwesomeIcon icon="print" />}
                                    onClick={() => {
                                        printDocument(saleReceiptRef.current, {
                                            documentType: 'thermal-80mm',
                                            title: `ใบเสร็จรับเงิน ${lastReceipt?.bill_no || ''}`
                                        });
                                    }}
                                    style={{ borderRadius: '10px', fontWeight: 600 }}
                                    data-testid="btn-print-receipt-80mm"
                                >
                                    พิมพ์ใบเสร็จ (80mm)
                                </Button>
                                <Button 
                                    type="default"
                                    onClick={handleCloseReceiptModal}
                                    style={{ borderRadius: '10px', fontWeight: 600 }}
                                    data-testid="btn-close-receipt-modal"
                                >
                                    ➕ เริ่มบิลใหม่
                                </Button>
                            </div>
                        </div>
                    </>
                )}
            </Modal>

            <Modal
                closeIcon={false}
                centered
                open={isOpenWorkShift}
                footer={null}
                width={'880px'}
                style={{ maxWidth: '95vw' }}
                destroyOnClose={true}
            >
                <WorkShift lock={lock} onClosed={() => {setIsOpenWorkShift(false)}} onSaved={() => {setIsOpenWorkShift(false)}} />
            </Modal>

            <Modal
                closeIcon={false}
                centered
                open={isOpenShortcut}
                footer={setFooter()}
                width={'70%'}
                destroyOnClose={true}
            >
                <ShortCut onSelected={(e: any) => {onManageProduct('A', e); setIsOpenShortcut(false);}} />
            </Modal>
        </>
    );
}

export default Sale;